/* dev-transparency-reviews.js */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.transparency-reviews').forEach(section => {
    if (section.hasAttribute('data-judgeme')) {
      // Swap in live reviews first, then lay out the marquee against the real
      // cards — init() measures card heights, so it has to run afterwards.
      loadJudgeMeReviews(section)
        .catch(() => {})
        .finally(() => requestAnimationFrame(() => init(section)));
    } else {
      requestAnimationFrame(() => init(section));
    }
  });
});

/* ── Judge.me ──────────────────────────────────────────────────────────────
   Judge.me stores no structured review metafield (only cached widget HTML)
   and its JSON review API rejects public tokens:

     GET /api/v1/reviews        -> 403 "public token ... not enough permissions"

   The widget API does accept the public token, but answers with rendered HTML
   rather than JSON, so the markup is parsed and mapped onto our own cards:

     all products -> /api/v1/widgets/all_reviews_page  -> { all_reviews: html }
     one product  -> /api/v1/widgets/product_review    -> { widget: html }

   The token is Judge.me's PUBLIC token — read-only and intended for front-end
   use. Verified: both endpoints 200, and CORS reflects the shop origin.

   Any failure — missing token, network error, no reviews — leaves the
   manually written cards already in the markup untouched.
   ────────────────────────────────────────────────────────────────────────── */

const JUDGEME_WIDGET_API = 'https://judge.me/api/v1/widgets';

async function loadJudgeMeReviews(section) {
  const token = section.dataset.judgemeToken;
  const shop = section.dataset.judgemeShop;
  if (!token || !shop) return;

  const limit = Math.min(parseInt(section.dataset.judgemeCount, 10) || 12, 100);
  const productId = section.dataset.judgemeProductId;

  const params = new URLSearchParams({ api_token: token, shop_domain: shop });
  let endpoint;
  if (productId) {
    endpoint = `${JUDGEME_WIDGET_API}/product_review`;
    params.set('external_id', productId);
  } else {
    endpoint = `${JUDGEME_WIDGET_API}/all_reviews_page`;
    params.set('per_page', String(limit));
  }

  const response = await fetch(`${endpoint}?${params}`);
  if (!response.ok) throw new Error(`Judge.me responded ${response.status}`);

  const payload = await response.json();
  const html = payload.all_reviews || payload.widget || '';
  const reviews = parseJudgeMeReviews(html).slice(0, limit);
  if (!reviews.length) return;

  renderJudgeMeCards(section, reviews);
}

function parseJudgeMeReviews(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  return Array.from(doc.querySelectorAll('.jdgm-rev'))
    .map(node => {
      const text = selector => (node.querySelector(selector)?.textContent || '').trim();
      const ratingNode = node.querySelector('.jdgm-rev__rating');
      return {
        rating: parseInt(ratingNode?.getAttribute('data-score') || '', 10) || 0,
        name: text('.jdgm-rev__author'),
        // Fall back to the review title when someone rated without writing a body.
        body: text('.jdgm-rev__body') || text('.jdgm-rev__title')
      };
    })
    .filter(review => review.body);
}

function escapeHtml(value) {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function starsMarkup(rating, hidden) {
  const filled = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  const star = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M13.6392 6.17741L10.8267 8.60429L11.6836 12.2337C11.7308 12.4307 11.7187 12.6373 11.6486 12.8274C11.5785 13.0175 11.4536 13.1826 11.2898 13.3018C11.1259 13.4209 10.9304 13.4889 10.728 13.497C10.5255 13.5051 10.3252 13.4531 10.1523 13.3474L6.99605 11.4049L3.83793 13.3474C3.66507 13.4525 3.46499 13.504 3.26289 13.4956C3.06079 13.4872 2.8657 13.4191 2.70218 13.3001C2.53867 13.181 2.41404 13.0162 2.344 12.8264C2.27395 12.6367 2.26162 12.4304 2.30855 12.2337L3.16856 8.60429L0.356055 6.17741C0.203116 6.04523 0.0925079 5.87092 0.0380438 5.67625C-0.0164202 5.48159 -0.0123263 5.27519 0.0498142 5.08283C0.111955 4.89048 0.229388 4.72069 0.387448 4.59468C0.545508 4.46867 0.73719 4.39201 0.938555 4.37429L4.62605 4.07679L6.04855 0.634288C6.12555 0.446674 6.2566 0.286193 6.42504 0.173249C6.59347 0.0603055 6.79169 0 6.99449 0C7.19729 0 7.39551 0.0603055 7.56395 0.173249C7.73239 0.286193 7.86343 0.446674 7.94043 0.634288L9.3623 4.07679L13.0498 4.37429C13.2516 4.39135 13.4438 4.46758 13.6024 4.59342C13.7611 4.71925 13.8791 4.88911 13.9416 5.0817C14.0041 5.27429 14.0084 5.48104 13.954 5.67607C13.8995 5.87109 13.7887 6.04571 13.6354 6.17804L13.6392 6.17741Z" fill="currentColor"/></svg>`;

  let out = '';
  for (let i = 1; i <= 5; i += 1) {
    out += `<span class="transparency-reviews__star${i <= filled ? ' transparency-reviews__star--filled' : ''}" aria-hidden="true">${star}</span>`;
  }

  const label = hidden
    ? 'aria-hidden="true"'
    : `role="img" aria-label="${filled} out of 5 stars"`;
  return `<div class="transparency-reviews__stars" ${label}>${out}</div>`;
}

function judgeMeCardMarkup(review, hidden) {
  const initial = review.name ? review.name.charAt(0).toUpperCase() : '';

  return `
    <article class="transparency-reviews__card"${hidden ? ' aria-hidden="true"' : ''}>
      <div class="transparency-reviews__card-overlay">
        ${starsMarkup(review.rating, hidden)}
        <div class="transparency-reviews__card-content">
          <p class="transparency-reviews__quote">${escapeHtml(review.body)}</p>
          ${
            review.name
              ? `<div class="transparency-reviews__reviewer-row">
                   ${initial ? `<div class="transparency-reviews__avatar transparency-reviews__avatar--initial" aria-hidden="true">${escapeHtml(initial)}</div>` : ''}
                   <div class="transparency-reviews__reviewer-meta">
                     <p class="transparency-reviews__reviewer">${escapeHtml(review.name)}</p>
                   </div>
                 </div>`
              : ''
          }
        </div>
      </div>
    </article>`;
}

function renderJudgeMeCards(section, reviews) {
  const tracks = section.querySelectorAll('.transparency-reviews__track');
  if (!tracks.length) return;

  // Each track holds the set twice so the desktop marquee loops seamlessly.
  const real = reviews.map(review => judgeMeCardMarkup(review, false)).join('');
  const clones = reviews.map(review => judgeMeCardMarkup(review, true)).join('');

  tracks.forEach((track, index) => {
    // Row 2 is aria-hidden as a whole, so every card in it is a clone.
    track.innerHTML = index === 0 ? real + clones : clones + clones;
  });

  section.setAttribute('data-judgeme-loaded', String(reviews.length));
}

function init(section) {
  if (!window.matchMedia('(max-width: 600px)').matches) return;

  const row = section.querySelector('[data-scroll-container]');
  if (!row) return;

  const cards = Array.from(
    row.querySelectorAll('.transparency-reviews__card:not([aria-hidden])')
  );
  if (!cards.length) return;

  const progressFill = section.querySelector('[data-progress-fill]');
  const STEP = cards[0].offsetWidth + 8; // cardWidth + gap

  // ── Lock row height to the tallest possible state across all cards ───────
  // Measure every card in active state (no transition) and use the maximum.
  let lockedHeight = 0;
  cards.forEach(card => {
    const overlay = card.querySelector('.transparency-reviews__card-overlay');
    if (overlay) overlay.style.transition = 'none';
    card.classList.add('is-active');
    void card.offsetHeight;
    if (card.offsetHeight > lockedHeight) lockedHeight = card.offsetHeight;
    card.classList.remove('is-active');
    void card.offsetHeight;
    if (overlay) overlay.style.transition = '';
  });
  section.style.setProperty('--tr-row-height', `${lockedHeight}px`);
  // ─────────────────────────────────────────────────────────────────────────

  function getActiveIndex() {
    // With padding = (containerWidth - cardWidth) / 2, scroll offset is exactly 0,
    // so card[i] snaps at scrollLeft = i * STEP.
    return Math.max(0, Math.min(
      Math.round(row.scrollLeft / STEP),
      cards.length - 1
    ));
  }

  function updateActive() {
    const idx = getActiveIndex();
    cards.forEach((card, i) => card.classList.toggle('is-active', i === idx));
    if (progressFill) {
      progressFill.style.width = `${(idx + 1) / cards.length * 100}%`;
    }
  }

  // Start centred on the middle card
  const midIndex = Math.floor(cards.length / 2);
  row.scrollLeft = midIndex * STEP;

  row.addEventListener('scroll', updateActive, { passive: true });
  updateActive();
}
